import React, { useState } from 'react';
import { IconArrowHead } from '../../../components';
import GuideCard from '../GuideCard/GuideCard';
import css from './GuideCarousel.module.css';

/**
 * GuideCarousel component displays a carousel of guide cards with smooth transitions
 * @param {Array} guides - Array of guide listing objects
 * @param {Object} intl - React Intl object for translations
 * @param {Object} config - Application configuration
 * @param {number} itemsToShow - Number of items visible at once (default 2)
 */
const GuideCarousel = ({ guides, intl, config, itemsToShow = 2 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const maxIndex = Math.max(0, guides.length - itemsToShow);

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  // Only show arrows if there are more than 2 guides
  const showArrows = guides.length > itemsToShow;

  // Calculate transform for smooth sliding
  const translateX = -(currentIndex * (100 / itemsToShow));
  const gapPx = 12;
  const slideWidth =
    itemsToShow === 1
      ? '100%'
      : `calc(${100 / itemsToShow}% - ${((itemsToShow - 1) * gapPx) / itemsToShow}px)`;

  return (
    <div className={css.carouselContainer}>
      {showArrows && (
        <button
          onClick={handlePrevious}
          className={css.arrowButton}
          aria-label={intl.formatMessage({ id: 'GuideCarousel.previous' })}
        >
          <IconArrowHead direction="left" size="big" />
        </button>
      )}

      <div className={css.carouselContent}>
        <div 
          className={css.guidesTrack}
          style={{ transform: `translateX(${translateX}%)` }}
        >
          {guides.map(guide => (
            <div key={guide.id.uuid} className={css.guideSlide} style={{ flex: `0 0 ${slideWidth}` }}>
              <GuideCard
                listing={guide}
                intl={intl}
                config={config}
              />
            </div>
          ))}
        </div>
      </div>

      {showArrows && (
        <button
          onClick={handleNext}
          className={css.arrowButton}
          aria-label={intl.formatMessage({ id: 'GuideCarousel.next' })}
        >
          <IconArrowHead direction="right" size="big" />
        </button>
      )}
    </div>
  );
};

export default GuideCarousel;